import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { captureSmartTableEvent } from "./analytics";
import { SmartTableDetail, unwrapSmartTableEntityPayload } from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export interface DuplicateSmartTableRequest {
  tableId: string;
  title?: string;
  includeComputedValues?: boolean;
}

export const useDuplicateSmartTable = () => {
  const userToken = useAuth()?.userToken ?? "";
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wid = Number(workspaceId);
  const queryClient = useQueryClient();

  return useMutation<SmartTableDetail, unknown, DuplicateSmartTableRequest>({
    mutationFn: async ({ tableId, title, includeComputedValues = false }) => {
      const response = await fetch(ENDPOINTS.smart_table_duplicate(wid, tableId), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({ title, include_computed_values: includeComputedValues }),
      });
      const responsePayload = await handleResponse(response);
      return unwrapSmartTableEntityPayload<SmartTableDetail>(responsePayload, "smart_table");
    },
    onSuccess: (duplicatedTable, variables) => {
      queryClient.invalidateQueries({ queryKey: ["smart-tables", wid, "list"] });
      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: ["folder-entities", workspaceId] });
      }
      captureSmartTableEvent("Smart Table Duplicated", {
        workspace_id: wid,
        smart_table_id: duplicatedTable.id,
        source_smart_table_id: variables.tableId,
        include_computed_values: Boolean(variables.includeComputedValues),
      });
    },
  });
};
